import { useRef } from 'react';
import { trackEvent } from '../../lib/usageEvents';
import { GRID_GAP_X_PX, GRID_GAP_Y_PX, parseTrackSizes, pointerToCell } from './gridLayout';

/** Геометрия сетки в момент начала переноса: края, ширина и высоты всех строк. */
const measureGrid = (grid, cols) => {
  const rect = grid.getBoundingClientRect();
  const style = window.getComputedStyle(grid);
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    cols,
    rowSizes: parseTrackSizes(style.gridTemplateRows),
    gapX: Number.parseFloat(style.columnGap) || GRID_GAP_X_PX,
    gapY: Number.parseFloat(style.rowGap) || GRID_GAP_Y_PX
  };
};

/**
 * Перенос и растягивание элемента указателем.
 *
 * Сдвиг считается в клетках от точки, где взяли элемент, по геометрии, снятой один раз в
 * начале: живая сетка во время переноса перестраивается, и пересчёт по ней перекидывал бы
 * элемент туда-обратно. Раскладка обновляется на каждой новой клетке — соседи расступаются
 * сразу, как в Grafana.
 *
 * DOM-события у переноса нет, поэтому событие аналитики шлётся явно, один раз за перенос.
 */
export const useLayoutDrag = ({ gridRef, cols, onMove, onResize }) => {
  const dragRef = useRef(null);

  const begin = (event, item, mode) => {
    if (event.button !== 0 || !gridRef.current) {
      return;
    }

    event.preventDefault();
    const geometry = measureGrid(gridRef.current, cols);
    dragRef.current = { item, mode, geometry, start: pointerToCell(event, geometry), last: '0:0' };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const move = (event) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }

    const cell = pointerToCell(event, drag.geometry);
    const columns = cell.column - drag.start.column;
    const rows = cell.row - drag.start.row;
    const key = `${columns}:${rows}`;
    if (key === drag.last) {
      return;
    }

    drag.last = key;
    if (drag.mode === 'move') {
      onMove(drag.item.id, { x: drag.item.x + columns, y: drag.item.y + rows });
    } else {
      onResize(drag.item.id, { w: drag.item.w + columns, h: drag.item.h + rows });
    }
  };

  const end = (event) => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }

    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (drag.last !== '0:0') {
      trackEvent(drag.mode === 'move' ? 'layout.item.drag' : 'layout.item.resize');
    }
  };

  return (item, mode) => ({
    onPointerDown: (event) => begin(event, item, mode),
    onPointerMove: move,
    onPointerUp: end,
    onPointerCancel: end
  });
};
